import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Video, Link as LinkIcon, Image, Download, Eye, Clock, Building2, Lock, AlertTriangle, Type } from 'lucide-react';
import { Button } from '../components/ui/button';
import { RichTextViewer } from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const CONTENT_TYPE_ICONS = {
    document: FileText,
    video: Video,
    link: LinkIcon,
    image: Image,
    richtext: Type,
};

export default function PublicDealRoom() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [room, setRoom] = useState(null);
    const [visitor, setVisitor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadRoom();
    }, [slug, token]);

    const loadRoom = async () => {
        if (!token) {
            setError('Missing access token. Please use the link from your invitation email.');
            setLoading(false);
            return;
        }
        
        try {
            const res = await axios.get(`${API}/public/room/${slug}?token=${token}`);
            setRoom(res.data.room);
            setVisitor(res.data.visitor);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('This deal room was not found or is no longer active.');
            } else if (err.response?.status === 403) {
                setError(err.response.data?.detail || 'You do not have access to this deal room.');
            } else {
                setError('Unable to load deal room. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (item) => {
        try {
            const res = await axios.get(`${API}/public/room/${slug}/item/${item.item_id}?token=${token}`);
            if (res.data.file_data) {
                const link = document.createElement('a');
                link.href = `data:application/octet-stream;base64,${res.data.file_data}`;
                link.download = res.data.file_name || item.title;
                link.click();
            } else if (res.data.external_url) {
                window.open(res.data.external_url, '_blank');
            }
        } catch (err) {
            alert('Unable to download this file.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
                    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                        {error.includes('access') ? (
                            <Lock className="w-8 h-8 text-red-500" />
                        ) : (
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        )}
                    </div>
                    <h1 className="text-xl font-bold text-stone-900 mb-2">Unable to Access</h1>
                    <p className="text-stone-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!room) return null;

    return (
        <div className="min-h-screen bg-stone-50">
            {/* Header */}
            <header className="bg-gradient-to-r from-stone-900 to-stone-700 text-white">
                <div className="max-w-5xl mx-auto px-4 py-8">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold mb-1">{room.name}</h1>
                            {room.account_name && (
                                <p className="text-white/80 flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    {room.account_name}
                                </p>
                            )}
                        </div>
                        <div className="text-right text-sm text-white/70">
                            <p>Welcome, {visitor?.name}</p>
                            <p className="text-xs">{visitor?.email}</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-5xl mx-auto px-4 py-8">
                {room.sections && room.sections.length > 0 ? (
                    <div className="space-y-8">
                        {room.sections.map(section => (
                            <section key={section.section_id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
                                <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
                                    <h2 className="text-lg font-semibold text-stone-900">{section.name}</h2>
                                    {section.description && (
                                        <p className="text-sm text-stone-500 mt-1">{section.description}</p>
                                    )}
                                </div>
                                
                                {section.items && section.items.length > 0 ? (
                                    <div className="divide-y divide-stone-100">
                                        {section.items.map(item => {
                                            const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || FileText;
                                            
                                            // Handle richtext content type differently
                                            if (item.content_type === 'richtext') {
                                                return (
                                                    <div key={item.item_id} className="px-6 py-6">
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                                                <Type className="w-5 h-5 text-emerald-600" />
                                                            </div>
                                                            <div>
                                                                <h3 className="text-base font-medium text-stone-900">{item.title}</h3>
                                                                {item.description && (
                                                                    <p className="text-sm text-stone-500">{item.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="pl-13 border-l-2 border-stone-100 ml-5">
                                                            <RichTextViewer content={item.rich_content} />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            return (
                                                <div 
                                                    key={item.item_id} 
                                                    className="flex items-center gap-4 px-6 py-4 hover:bg-stone-50 transition-colors"
                                                >
                                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                                        item.content_type === 'video' ? 'bg-violet-50' :
                                                        item.content_type === 'link' ? 'bg-blue-50' :
                                                        item.content_type === 'image' ? 'bg-amber-50' : 'bg-stone-100'
                                                    }`}>
                                                        <IconComponent className={`w-6 h-6 ${
                                                            item.content_type === 'video' ? 'text-violet-600' :
                                                            item.content_type === 'link' ? 'text-blue-600' :
                                                            item.content_type === 'image' ? 'text-amber-600' : 'text-stone-700'
                                                        }`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-base font-medium text-stone-900">{item.title}</h3>
                                                        {item.description && (
                                                            <p className="text-sm text-stone-500 mt-0.5 line-clamp-2">{item.description}</p>
                                                        )}
                                                        <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                                                            {item.file_name && <span>{item.file_name}</span>}
                                                            {item.file_size > 0 && <span>{formatFileSize(item.file_size)}</span>}
                                                        </div>
                                                    </div>
                                                    <Button 
                                                        onClick={() => handleDownload(item)}
                                                        variant="outline"
                                                        size="sm"
                                                        className="flex-shrink-0"
                                                    >
                                                        {item.content_type === 'link' ? (
                                                            <>
                                                                <Eye className="w-4 h-4 mr-1" />
                                                                View
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Download className="w-4 h-4 mr-1" />
                                                                Download
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="px-6 py-8 text-center text-stone-400">
                                        No content items in this section yet
                                    </div>
                                )}
                            </section>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-12 text-center">
                        <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-stone-700 mb-2">Content Coming Soon</h2>
                        <p className="text-stone-500">This deal room is being prepared for you. Check back soon!</p>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="max-w-5xl mx-auto px-4 py-8 text-center text-sm text-stone-400">
                <p>Powered by Nurture CRM</p>
                {room.expiration_date && (
                    <p className="mt-1">This room expires on {room.expiration_date}</p>
                )}
            </footer>
        </div>
    );
}
